import { Injectable } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';

import {
  SAAS_SUBSCRIPTION_CUSTOM_RULES,
  SAAS_SUBSCRIPTION_LIMITS_DATA_TYPES,
  SAAS_SUBSCRIPTION_METHODS,
  SAAS_SUBSCRIPTION_PAYMENT_TYPE,
  SAAS_SUBSCRIPTIONS_DURATIONS_TYPES,
} from '@shared/constants';
import {
  ILimitsQuotesConfig,
  ILimitsQuotesConfigDataTypes,
  ISaaSSubscriptionFeatures,
  ISaaSSubscriptionFeaturesLimits,
} from '@shared/interfaces';
import { saasLimitationGenerator } from '@shared/utils';

import {
  SaaSSubscriptionFeaturesFormGroupType,
  SaaSSubscriptionFeaturesFormType,
  SaaSSubscriptionFeaturesLimitsFormGroupType,
  SaaSSubscriptionFeaturesLimitsValueFormGroupType,
  SaaSSubscriptionFormGroupType,
} from '../types/form.type';

@Injectable({
  providedIn: 'root',
})
export class SaaSSubscriptionGeneralService {
  /** Get Subscription Form*/

  getForm(): SaaSSubscriptionFormGroupType {
    return new FormGroup({
      name: new FormControl(null, Validators.required),
      is_displayable: new FormControl(false),
      method: new FormControl(null, Validators.required),
      payment_type: new FormControl(null, Validators.required),
      duration_config: new FormControl(null, Validators.required),
      duration_mode: new FormControl(null, Validators.required),
      duration_type: new FormControl(null, Validators.required),
      duration_value: new FormControl(null, Validators.required),
      price: new FormControl(null, Validators.required),
      price_currency: new FormControl(null, Validators.required),
      fallback_plan: new FormControl(null, Validators.required),
      grace_period_type: new FormControl(null, Validators.required),
      grace_period_value: new FormControl(null, Validators.required),
      description: new FormControl(null, Validators.required),
      start_date: new FormControl(null, Validators.required),
      end_date: new FormControl(null, Validators.required),
      permissions: new FormControl(null),
    });
  }

  getFeaturesFormGroup(): SaaSSubscriptionFeaturesFormType {
    return new FormGroup({
      features: new FormArray([]),
    });
  }

  getFeaturesForm(): SaaSSubscriptionFeaturesFormGroupType {
    return new FormGroup({
      title: new FormControl(null, Validators.required),
      description: new FormControl(null, Validators.required),
      icon: new FormControl(null, Validators.required),
      is_displayable: new FormControl(false),
      limits: new FormArray<SaaSSubscriptionFeaturesLimitsFormGroupType>([]),
    });
  }

  getLimitsForm(
    value?: SAAS_SUBSCRIPTION_CUSTOM_RULES
  ): SaaSSubscriptionFeaturesLimitsFormGroupType {
    return new FormGroup({
      key: new FormControl<SAAS_SUBSCRIPTION_CUSTOM_RULES>(
        value ?? null,
        Validators.required
      ),
    });
  }

  /** Get Translations */
  getSubscriptionDisplayValue(key: SAAS_SUBSCRIPTION_METHODS): string {
    switch (key) {
      case SAAS_SUBSCRIPTION_METHODS.FREE_FOREVER:
        return 'saas-subscription.subscription-method.free-forever';

      case SAAS_SUBSCRIPTION_METHODS.FREE_TRIAL:
        return 'saas-subscription.subscription-method.free-trial';

      case SAAS_SUBSCRIPTION_METHODS.PAYABLE:
        return 'saas-subscription.subscription-method.payable';

      default:
        return '';
    }
  }

  getModeMethodDisplayValue(key: SAAS_SUBSCRIPTIONS_DURATIONS_TYPES): string {
    switch (key) {
      case SAAS_SUBSCRIPTIONS_DURATIONS_TYPES.MONTHS:
        return 'saas-subscription.mode.months';

      case SAAS_SUBSCRIPTIONS_DURATIONS_TYPES.WEEKS:
        return 'saas-subscription.mode.weeks';

      case SAAS_SUBSCRIPTIONS_DURATIONS_TYPES.DAYS:
        return 'saas-subscription.mode.days';
      case SAAS_SUBSCRIPTIONS_DURATIONS_TYPES.FREE:
        return 'free';
    }
  }

  getGracePeriodDisplayValue(key: SAAS_SUBSCRIPTIONS_DURATIONS_TYPES): string {
    switch (key) {
      case SAAS_SUBSCRIPTIONS_DURATIONS_TYPES.MONTHS:
        return 'saas-subscription.grace-type.months';

      case SAAS_SUBSCRIPTIONS_DURATIONS_TYPES.WEEKS:
        return 'saas-subscription.grace-type.weeks';

      case SAAS_SUBSCRIPTIONS_DURATIONS_TYPES.DAYS:
        return 'saas-subscription.grace-type.days';
      case SAAS_SUBSCRIPTIONS_DURATIONS_TYPES.FREE:
        return '';
    }
  }

  getPaymentTypeDisplayValue(key: SAAS_SUBSCRIPTION_PAYMENT_TYPE): string {
    switch (key) {
      case SAAS_SUBSCRIPTION_PAYMENT_TYPE.PER_SEAT:
        return 'saas-subscription.payment-type.per-seat';

      case SAAS_SUBSCRIPTION_PAYMENT_TYPE.DEFAULT:
        return 'saas-subscription.payment-type.default';
      case SAAS_SUBSCRIPTION_PAYMENT_TYPE.FREE:
        return null;
    }
  }

  /** Get Menu Options */
  getSubscriptionMethods(): {
    key: SAAS_SUBSCRIPTION_METHODS;
    translate: string;
  }[] {
    return Object.values(SAAS_SUBSCRIPTION_METHODS).map((e) => {
      return {
        key: e,
        translate: this.getSubscriptionDisplayValue(e),
      };
    });
  }

  getPaymentTypeMethods(): {
    key: SAAS_SUBSCRIPTION_PAYMENT_TYPE;
    translate: string;
  }[] {
    return Object.values(SAAS_SUBSCRIPTION_PAYMENT_TYPE)
      .filter((e) => e !== SAAS_SUBSCRIPTION_PAYMENT_TYPE.FREE)
      .map((e) => {
        return {
          key: e,
          translate: this.getPaymentTypeDisplayValue(e),
        };
      });
  }

  getModeMethods(): {
    key: SAAS_SUBSCRIPTIONS_DURATIONS_TYPES;
    translate: string;
  }[] {
    return Object.values(SAAS_SUBSCRIPTIONS_DURATIONS_TYPES)
      .filter((e) => e !== SAAS_SUBSCRIPTIONS_DURATIONS_TYPES.FREE)
      .map((e) => {
        return {
          key: e,
          translate: this.getModeMethodDisplayValue(e),
        };
      });
  }

  getGracePeriodTypeMethods(): {
    key: SAAS_SUBSCRIPTIONS_DURATIONS_TYPES;
    translate: string;
  }[] {
    return Object.values(SAAS_SUBSCRIPTIONS_DURATIONS_TYPES)
      .filter((e) => e !== SAAS_SUBSCRIPTIONS_DURATIONS_TYPES.FREE)
      .map((e) => {
        return {
          key: e,
          translate: this.getGracePeriodDisplayValue(e),
        };
      });
  }

  //get icons
  getMaterialIcons(): string[] {
    return [
      '3d_rotation',
      'ac_unit',
      'access_alarm',
      'access_alarms',
      'access_time',
      'accessibility',
      'accessibility_new',
      'accessible',
      'accessible_forward',
      'account_balance',
      'account_balance_wallet',
      'account_box',
      'account_circle',
      'account_tree',
      'ad_units',
      'adb',
      'add',
      'add_a_photo',
      'add_alarm',
      'add_alert',
      'add_box',
      'add_business',
      'add_call',
      'add_chart',
      'add_circle',
      'add_circle_outline',
      'add_comment',
      'add_ic_call',
      'add_link',
      'add_location',
      'add_location_alt',
      'add_moderator',
      'add_photo_alternate',
      'add_reaction',
      'add_road',
      'add_shopping_cart',
      'add_task',
      'add_to_drive',
      'add_to_home_screen',
      'add_to_photos',
      'add_to_queue',
      'addchart',
      'adjust',
      'admin_panel_settings',
      'ads_click',
      'agriculture',
      'air',
      'airline_seat_flat',
      'airline_seat_flat_angled',
      'airline_seat_individual_suite',
      'airline_seat_legroom_extra',
      'airline_seat_legroom_normal',
      'airline_seat_legroom_reduced',
      'airline_seat_recline_extra',
      'airline_seat_recline_normal',
      'airplanemode_active',
      'airplanemode_inactive',
      'airplay',
      'airport_shuttle',
      'alarm',
      'alarm_add',
      'alarm_off',
      'alarm_on',
      'album',
      'align_horizontal_center',
      'align_horizontal_left',
      'align_horizontal_right',
      'align_vertical_bottom',
      'align_vertical_center',
      'align_vertical_top',
      'all_inbox',
      'all_inclusive',
      'all_out',
      'alt_route',
      'alternate_email',
      'analytics',
      'anchor',
      'android',
      'animation',
      'announcement',
      'aod',
      'apartment',
      'api',
      'app_blocking',
      'app_registration',
      'app_settings_alt',
      'approval',
      'apps',
      'architecture',
      'archive',
      'area_chart',
      'arrow_back',
      'arrow_back_ios',
      'arrow_back_ios_new',
      'arrow_circle_down',
      'arrow_circle_left',
      'arrow_circle_right',
      'arrow_circle_up',
      'arrow_downward',
      'arrow_drop_down',
      'arrow_drop_down_circle',
      'arrow_drop_up',
      'arrow_forward',
      'arrow_forward_ios',
      'arrow_left',
      'arrow_right',
      'arrow_right_alt',
      'arrow_upward',
      'art_track',
      'article',
      'aspect_ratio',
      'assessment',
      'assignment',
      'assignment_ind',
      'assignment_late',
      'assignment_return',
      'assignment_returned',
      'assignment_turned_in',
      'assistant',
      'assistant_direction',
      'assistant_navigation',
      'assistant_photo',
      'atm',
      'attach_email',
      'attach_file',
      'attach_money',
      'attachment',
      'attractions',
      'attribution',
      'audiotrack',
      'auto_awesome',
      'auto_awesome_mosaic',
      'auto_awesome_motion',
      'auto_delete',
      'auto_fix_high',
      'auto_fix_normal',
      'auto_fix_off',
      'auto_graph',
      'auto_stories',
      'autorenew',
      'av_timer',
      'baby_changing_station',
      'backpack',
      'backspace',
      'backup',
      'backup_table',
      'badge',
      'bakery_dining',
      'balcony',
      'ballot',
      'bar_chart',
      'batch_prediction',
      'bathroom',
      'bathtub',
      'battery_alert',
      'battery_charging_full',
      'battery_full',
      'battery_saver',
      'battery_std',
      'battery_unknown',
      'beach_access',
      'bed',
      'bedroom_baby',
      'bedroom_child',
      'bedroom_parent',
      'bedtime',
      'beenhere',
      'bento',
      'bike_scooter',
      'biotech',
      'blender',
      'block',
      'bloodtype',
      'bluetooth',
      'bluetooth_audio',
      'bluetooth_connected',
      'bluetooth_disabled',
      'bluetooth_drive',
      'bluetooth_searching',
      'blur_circular',
      'blur_linear',
      'blur_off',
      'blur_on',
      'bolt',
      'book',
      'book_online',
      'bookmark',
      'bookmark_add',
      'bookmark_added',
      'bookmark_border',
      'bookmark_remove',
      'bookmarks',
      'border_all',
      'border_bottom',
      'border_clear',
      'border_color',
      'border_horizontal',
      'border_inner',
      'border_left',
      'border_outer',
      'border_right',
      'border_style',
      'border_top',
      'border_vertical',
      'branding_watermark',
      'breakfast_dining',
      'brightness_1',
      'brightness_2',
      'brightness_3',
      'brightness_4',
      'brightness_5',
      'brightness_6',
      'brightness_7',
      'brightness_auto',
      'brightness_high',
      'brightness_low',
      'brightness_medium',
      'broken_image',
      'browser_not_supported',
      'brunch_dining',
      'brush',
      'bubble_chart',
      'bug_report',
      'build',
      'build_circle',
      'bungalow',
      'burst_mode',
      'bus_alert',
      'business',
      'business_center',
      'cabin',
      'cable',
      'cached',
      'cake',
      'calculate',
      'calendar_today',
      'calendar_view_day',
      'calendar_view_month',
      'calendar_view_week',
      'call',
      'call_end',
      'call_made',
      'call_merge',
      'call_missed',
      'call_missed_outgoing',
      'call_received',
      'call_split',
      'call_to_action',
      'camera',
      'camera_alt',
      'camera_enhance',
      'camera_front',
      'camera_indoor',
      'camera_outdoor',
      'camera_rear',
      'camera_roll',
      'cameraswitch',
      'campaign',
      'cancel',
      'cancel_presentation',
      'cancel_schedule_send',
      'candlestick_chart',
      'car_rental',
      'car_repair',
      'card_giftcard',
      'card_membership',
      'card_travel',
      'carpenter',
      'cases',
      'casino',
      'cast',
      'cast_connected',
      'cast_for_education',
      'catching_pokemon',
      'category',
      'celebration',
      'cell_wifi',
      'center_focus_strong',
      'center_focus_weak',
      'chair',
      'chair_alt',
      'chalet',
      'change_circle',
      'change_history',
      'charging_station',
      'chat',
      'chat_bubble',
      'chat_bubble_outline',
      'check',
      'check_box',
      'check_box_outline_blank',
      'check_circle',
      'check_circle_outline',
      'checklist',
      'checklist_rtl',
      'checkroom',
      'chevron_left',
      'chevron_right',
      'child_care',
      'child_friendly',
      'chrome_reader_mode',
      'circle',
      'circle_notifications',
      'class',
      'clean_hands',
      'cleaning_services',
      'clear',
      'clear_all',
      'close',
      'close_fullscreen',
      // The list continues...
    ];
  }

  //limits formControl
  getLimitSingleControl(
    config: ILimitsQuotesConfigDataTypes,
    value?: any
  ): FormControl {
    switch (config.type) {
      case SAAS_SUBSCRIPTION_LIMITS_DATA_TYPES.BOOLEAN:
        return new FormControl<boolean>(
          value || false,
          config.required ? [Validators.required] : []
        );

      case SAAS_SUBSCRIPTION_LIMITS_DATA_TYPES.STRING:
        return new FormControl<string>(
          value || null,
          config.required ? [Validators.required] : []
        );

      case SAAS_SUBSCRIPTION_LIMITS_DATA_TYPES.DATE:
        return new FormControl<Date>(
          value || null,
          config.required ? [Validators.required] : []
        );

      case SAAS_SUBSCRIPTION_LIMITS_DATA_TYPES.NUMBER:
        return new FormControl<number>(
          value || null,
          config.required ? [Validators.required] : []
        );

      case SAAS_SUBSCRIPTION_LIMITS_DATA_TYPES.DROP_DOWN:
        return new FormControl<string>(
          value || null,
          config.required ? [Validators.required] : []
        );

      default:
        return new FormControl<string>(null);
    }
  }

  getSaaSConfigsAndControls(
    key: SAAS_SUBSCRIPTION_CUSTOM_RULES,
    currentValue?: ISaaSSubscriptionFeaturesLimits
  ): {
    config: ILimitsQuotesConfig;
    form: SaaSSubscriptionFeaturesLimitsValueFormGroupType;
  } {
    const config = saasLimitationGenerator().find((e) => e.key === key);
    if (!config) {
      return { config: null, form: null };
    }

    const form: SaaSSubscriptionFeaturesLimitsValueFormGroupType =
      new FormGroup({
        val_1: this.getLimitSingleControl(config.val_1, currentValue?.val_1),
      });

    if (config.val_2) {
      form.addControl(
        'val_2',
        this.getLimitSingleControl(config.val_2, currentValue?.val_2)
      );
    }

    if (config.val_3) {
      form.addControl(
        'val_3',
        this.getLimitSingleControl(config.val_3, currentValue?.val_3)
      );
    }

    if (config.val_4) {
      form.addControl(
        'val_4',
        this.getLimitSingleControl(config.val_4, currentValue?.val_4)
      );
    }

    if (config.val_5) {
      form.addControl(
        'val_5',
        this.getLimitSingleControl(config.val_5, currentValue?.val_5)
      );
    }

    return { config, form };
  }

  getSaaSLimitsOptions(): {
    default: {
      key: SAAS_SUBSCRIPTION_CUSTOM_RULES;
      displayValue: string;
    }[];
    non_default: {
      key: SAAS_SUBSCRIPTION_CUSTOM_RULES;
      displayValue: string;
      hidden: boolean;
    }[];
  } {
    const config = saasLimitationGenerator();

    return {
      default: config
        .filter((e) => e.required)
        .map((e) => {
          return { key: e.key, displayValue: e.displayValue };
        }),
      non_default: config
        .filter((e) => !e.required)
        .map((e) => {
          return { key: e.key, displayValue: e.displayValue, hidden: false };
        }),
    };
  }

  //extracting control values
  extractFeatureLimitValues(
    form: FormArray<SaaSSubscriptionFeaturesLimitsFormGroupType>
  ): ISaaSSubscriptionFeaturesLimits[] {
    return form.controls.map((_e) => {
      let data: ISaaSSubscriptionFeaturesLimits = {
        key: _e.controls.key.value,
        val_1: _e.controls.values.value?.val_1,
      };

      if (_e.controls.values.value?.val_2)
        data = { ...data, val_2: _e.controls.values.value?.val_2 };

      if (_e.controls.values.value?.val_3)
        data = { ...data, val_3: _e.controls.values.value?.val_3 };

      if (_e.controls.values.value?.val_4)
        data = { ...data, val_4: _e.controls.values.value?.val_4 };

      if (_e.controls.values.value?.val_5)
        data = { ...data, val_5: _e.controls.values.value?.val_5 };

      return data;
    });
  }
  extractFeatureValues(
    form: FormArray<SaaSSubscriptionFeaturesFormGroupType>
  ): ISaaSSubscriptionFeatures[] {
    return form.controls.map((e) => {
      return {
        title: e.controls.title.value,
        description: e.controls.description.value,
        icon: e.controls.icon.value,
        is_displayable: e.controls.is_displayable.value,
        limits: this.extractFeatureLimitValues(e.controls.limits),
      };
    });
  }
}
